// @flow
import BrandInstanceStorage from "../helper/storage/BrandInstanceStorage";
import {resetVehicle, setVehicle} from "./vehicle";
import {loadConfig} from "./config";
import type {ApplicationState} from "../reducers";
import ProductApi, {INTENT} from "../api/ProductApi";
import RootNodeProvider from "../helper/RootNodeProvider";
import {RoleTypes} from "../models/Role";
import {offerCategoryKeyFor} from "../models/Marker";
import {
    popularProductsErrorGotten,
    popularProductsRequested,
    popularProductsSuccessGotten,
    productsErrorGotten,
    productSet,
    productsRequested,
    productsSet
} from "../reducers/contentReducer";
import history from "../history";

// Monotonically increasing tokens that identify the most recently dispatched
// products / popular-products request. When a category (or other filter) is
// switched while a previous request is still in flight, the older response
// must be discarded — otherwise it would be appended to (or overwrite) the
// newer category's product list. The .then handlers compare their captured
// token against the latest value and ignore stale responses.
let productsRequestToken = 0;
let popularProductsRequestToken = 0;

export const getPopularProducts = () => {
    return (dispatch, getState: () => ApplicationState) => {
        const {filter} = getState().filter;
        const {
            dealerId,
            moduleLanguage,
            role
        } = getState().config;
        const {pickupLocation} = getState().location;
        const {
            vehicle,
            environment
        } = RootNodeProvider.getRootNode().dataset;
        const intent = environment !== "own" ? INTENT.CATALOG_ONLY : INTENT.DEFAULT;
        const productApi = new ProductApi(dealerId, {
            configIdType: role,
            intent: intent,
            language: moduleLanguage,
            pickupLocation
        });
        let filterVehicle = null;

        if (vehicle) {
            dispatch(setVehicle(vehicle, null, "data_param"));
            filterVehicle = {
                series: vehicle,
                model: null
            };
            RootNodeProvider.getRootNode().removeAttribute("data-vehicle");
        }

        const filterOptions = {
            filterParameters: {
                categories: filter.categories,
                vehicle: filterVehicle,
                vehicleKey: filterVehicle ? null : filter.vehicleKey,
            },
        };

        const request = productApi.getOverview(filterOptions);
        const myToken = ++popularProductsRequestToken;

        dispatch(popularProductsRequested());

        return request.then(
            response => {
                if (myToken !== popularProductsRequestToken) return;
                dispatch(popularProductsSuccessGotten(
                    {
                        popularProducts: response.categoryTopProducts,
                        filterStats: response.filterStats,
                        salesProducts: response.markedProducts.find(item => item.category.key === offerCategoryKeyFor(role))
                    })
                );
            },
            error => {
                if (myToken !== popularProductsRequestToken) return;
                console.error(error);
                dispatch(popularProductsErrorGotten(error));
            }
        );
    };
};

export const getProducts = () => {
    return (dispatch, getState: () => ApplicationState) => {
        const {
            dealerId,
            moduleLanguage,
            role
        } = getState().config;
        const {currentPage} = getState().content;
        const {filter} = getState().filter;
        const {pickupLocation} = getState().location;
        const environment = getState().config.environment;
        const intent = environment !== "own" ? INTENT.CATALOG_ONLY : INTENT.DEFAULT;
        const productApi = new ProductApi(dealerId, {
            configIdType: role,
            intent: intent,
            language: moduleLanguage,
            pickupLocation
        });
        const filterOptions = {
            fuzzy: filter.searchPhrase === "undefined" ? "" : filter.searchPhrase,
            filterParameters: {
                intent: null,
                categories: filter.categories,
                categoryKey: filter.categoryKey,
                priceRange: filter.priceRange,
                markers: filter.markers,
                vehicleKey: filter.vehicleKey,
                vehicle: {
                    series: filter.vehicle,
                    model: filter.vehicleModel
                },
            },
            sortOrders: [{
                fieldname: filter.sort.fieldname,
                direction: filter.sort.direction
            }]
        };

        const myToken = ++productsRequestToken;
        dispatch(productsRequested());
        productApi.getProductsWithFilter(currentPage, filterOptions)
            .then(
                response => {
                    if (myToken !== productsRequestToken) return;
                    dispatch(productsSet({
                        products: response.pagedProducts.productItems,
                        filterStats: response.filterStats,
                        totalPages: response.pagedProducts.totalPages,
                        totalItems: response.pagedProducts.totalItems,
                    }));
                },
                error => {
                    if (myToken !== productsRequestToken) return;
                    console.error(error);
                    dispatch(productsErrorGotten(error));
                }
            );
    };
};

export const getProductOnLoad = (articleNumber, dealerId) => {
    return (dispatch, getState) => {
        const configId = dealerId || getState().config.garageId || getState().config.garageGroupId;
        const {filter} = getState().filter;
        const {
            moduleLanguage,
            role
        } = getState().config;
        const {pickupLocation} = getState().location;
        const filterOptions = {
            vehicle: filter.vehicleKey ? null : {
                series: filter.vehicle,
                model: filter.vehicleModel
            },
            vehicleKey: filter.vehicleKey,
        };

        if (!configId) return;
        const environment = getState().config.environment;
        const intent = environment !== "own" ? INTENT.CATALOG_ONLY : INTENT.DEFAULT;

        const productApi = new ProductApi(configId, {
            configIdType: role,
            intent: intent,
            language: moduleLanguage,
            pickupLocation
        });
        const request = productApi.getDealerProductDetailWithFilter(articleNumber, filterOptions);


        return request.then(
            response => {
                const Storage = new BrandInstanceStorage("garageID");
                Storage.set(response.product.dealerId, response.product.dealerId);
                if (!getState().config.dealerDetails?.dealership.groupMembers) {
                    dispatch(resetVehicle());
                    dispatch(loadConfig(response.product.dealerId, role === RoleTypes.GARAGE_GROUP ? RoleTypes.GROUP_DEALER : RoleTypes.DEALER, getState().config.garageGroupId));
                } else {
                    const applicableGroupMember = getState().config.dealerDetails.dealership.groupMembers.find(groupMember => groupMember.shippingForGroup);
                    if (!configId) {
                        console.error("No Shipping Dealer Found");
                    } else {
                        console.log(applicableGroupMember)
                        dispatch(resetVehicle());
                        dispatch(loadConfig(applicableGroupMember.cfgKey, role === RoleTypes.GARAGE_GROUP ? RoleTypes.GROUP_DEALER : RoleTypes.DEALER, getState().config.garageGroupId));
                    }
                }
                if (articleNumber !== response.product.id) {
                    console.warn("article number mismatch, reload product: ", articleNumber, response.product.id);
                    history.pushProduct(response.product.id, response.product.name);
                }
            },
            error => {
                console.error(error);
                dispatch(productsErrorGotten(error));
            }
        );
    };
};

export const getProduct = articleNumber => {
    return (dispatch, getState: () => ApplicationState) => {
        const {
            dealerId,
            role,
            moduleLanguage
        } = getState().config;
        const {filter} = getState().filter;
        const {pickupLocation} = getState().location;
        const environment = getState().config.environment;
        const intent = environment !== "own" ? INTENT.CATALOG_ONLY : INTENT.DEFAULT;

        const productApi = new ProductApi(dealerId, {
            configIdType: role,
            intent: intent,
            language: moduleLanguage,
            // eslint-disable-next-line no-restricted-globals
            location,
            pickupLocation
        });
        const filterOptions = {
            vehicle: filter.vehicleKey ? null : {
                series: filter.vehicle,
                model: filter.vehicleModel
            },
            vehicleKey: filter.vehicleKey,
        };

        return productApi.getDealerProductDetailWithFilter(articleNumber, filterOptions)
            .then(response => {
                if (articleNumber !== response.product.id) {
                    console.warn("article number mismatch, reload product: ", articleNumber, response.product.id);
                    history.pushProduct(response.product.id, response.product.name);
                } else {
                    dispatch(productSet(response));
                }
            })
            .catch(error => {
                console.error(error);
                dispatch(productsErrorGotten(error));
            });
    };
};
